import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { useDebounce } from '@/hooks/useDebounce';
import { useInfiniteLeads } from '@/hooks/leads/useInfiniteLeads';
import { useMergeLead } from '@/hooks/leads/useMergeLead';
import { cn } from '@/lib/utils';
import type { Lead } from '@/types/lead';

function getContactName(lead: Lead) {
  return (
    lead.contact?.displayName ??
    [lead.contact?.firstName, lead.contact?.lastName].filter(Boolean).join(' ') ??
    ''
  );
}

function leadTitle(lead: Lead) {
  return lead.title || lead.serviceRequested || 'Untitled lead';
}

function formatMergeCandidateLabel(lead: Lead) {
  const contactName = getContactName(lead);
  const title = leadTitle(lead);
  return [lead.leadNumber, title, contactName].filter(Boolean).join(' - ');
}

// The `/leads` list has no server-side "eligible merge candidate" filter, so
// eligibility is applied client-side (below). Because that filter runs after
// each backend page, a page full of ineligible (won/converted/archived) leads
// could hide a mergeable duplicate on a later page. So we page through results
// until we have MAX_VISIBLE_CANDIDATES eligible rows or run out — bounded by
// MAX_CANDIDATE_PAGES so a huge ineligible set can't fetch endlessly.
const CANDIDATE_FETCH_LIMIT = 50;
const MAX_VISIBLE_CANDIDATES = 20;
const MAX_CANDIDATE_PAGES = 5;

/**
 * A lead is eligible to be absorbed as a duplicate only if the backend merge
 * guard would accept it (`leads.service.merge`): not the survivor itself, not
 * archived, and not won/converted. Filtering here keeps the picker from
 * offering leads the server will reject with a 409/400.
 */
function isEligibleDuplicate(candidate: Lead, survivorId: string) {
  return (
    candidate.id !== survivorId &&
    !candidate.isArchived &&
    !(candidate.stage?.isWon || candidate.convertedEstimateId || candidate.convertedJobId)
  );
}

export function MergeLeadModal({
  open,
  onOpenChange,
  survivor,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  survivor: Lead;
  onSuccess?: () => void;
}) {
  const [selectedDuplicate, setSelectedDuplicate] = useState<Lead | null>(null);
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState('');
  const [allowCrossContact, setAllowCrossContact] = useState(false);
  const mergeLead = useMergeLead();

  // Server-driven search: typing filters by lead number / contact / title. The
  // picker resolves the real lead id under the hood so the user never needs a
  // UUID. Only fetch once there's a query (the list is search-gated).
  const debouncedSearch = useDebounce(search, 250);
  const trimmedSearch = debouncedSearch.trim();
  const leadsQuery = useInfiniteLeads(
    {
      search: trimmedSearch || undefined,
      limit: CANDIDATE_FETCH_LIMIT,
      sortBy: 'createdAt',
      sortDir: 'desc',
    },
    { enabled: open && trimmedSearch.length > 0 },
  );
  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } = leadsQuery;

  const eligible = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((page) => page.data)
        .filter((lead) => isEligibleDuplicate(lead, survivor.id)),
    [data, survivor.id],
  );
  const candidates = eligible.slice(0, MAX_VISIBLE_CANDIDATES);

  // Keep paging until we have enough eligible rows (or run out) so a page of
  // ineligible leads can't hide a mergeable duplicate on a later page.
  useEffect(() => {
    const pagesFetched = data?.pages.length ?? 0;
    if (
      eligible.length < MAX_VISIBLE_CANDIDATES &&
      hasNextPage &&
      !isFetchingNextPage &&
      pagesFetched > 0 &&
      pagesFetched < MAX_CANDIDATE_PAGES
    ) {
      void fetchNextPage();
    }
  }, [eligible.length, hasNextPage, isFetchingNextPage, data?.pages.length, fetchNextPage]);

  // Still "searching" while the first page loads, or while paging for more
  // eligible rows before any have surfaced.
  const isSearching = isLoading || (isFetchingNextPage && candidates.length === 0);

  const isCrossContact =
    selectedDuplicate != null && selectedDuplicate.contactId !== survivor.contactId;
  const needsCrossContactOptIn = isCrossContact && !allowCrossContact;
  const canSubmit = !mergeLead.isPending && selectedDuplicate != null && !needsCrossContactOptIn;

  function resetForm() {
    setSelectedDuplicate(null);
    setSearch('');
    setReason('');
    setAllowCrossContact(false);
  }

  // The results list shows only while there's a search query — so the modal
  // never opens with a list expanded, and clearing the query collapses it.
  const showResults = search.trim().length > 0;

  function closeModal() {
    resetForm();
    onOpenChange(false);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || !selectedDuplicate) return;

    mergeLead.mutate(
      {
        survivorLeadId: survivor.id,
        duplicateLeadId: selectedDuplicate.id,
        ...(allowCrossContact ? { allowCrossContact: true } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      },
      {
        onSuccess: () => {
          closeModal();
          onSuccess?.();
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (value) {
          onOpenChange(true);
          return;
        }
        closeModal();
      }}
    >
      {/* Cap the height and scroll: with the inline results list the modal can
          grow past a short viewport, otherwise pushing the Cancel/Merge footer
          off-screen. */}
      <DialogContent>
        {/* min-w-0: the DialogContent is a grid, so without it a long,
            unbreakable candidate title expands this form past the dialog width
            instead of letting the results list truncate. */}
        <form className="min-w-0 space-y-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Merge duplicate lead</DialogTitle>
            <DialogDescription>Survivor: {formatMergeCandidateLabel(survivor)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="duplicate-lead-search">Duplicate lead</Label>
              {selectedDuplicate ? (
                <div className="flex animate-in items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm fade-in-0 duration-150 motion-reduce:animate-none">
                  <span className="truncate">{formatMergeCandidateLabel(selectedDuplicate)}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto shrink-0 px-2 py-1 text-xs"
                    onClick={() => {
                      setSelectedDuplicate(null);
                      setAllowCrossContact(false);
                    }}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                // Rendered inline (not inside a Popover portal): a Popover
                // portals to document.body, outside the Dialog's scroll-lock
                // (react-remove-scroll), which blocks the wheel so the results
                // list can't scroll. Inline keeps the list within the Dialog so
                // it scrolls normally. shouldFilter=false — results come from the
                // server query, not cmdk's built-in local filtering.
                <Command shouldFilter={false} className="rounded-md border">
                  <CommandInput
                    id="duplicate-lead-search"
                    placeholder="Search lead # / contact / title…"
                    value={search}
                    onValueChange={setSearch}
                  />
                  {/* Smoothly expand/collapse the results by animating the grid
                      row from 0fr→1fr (height) plus opacity, so the list eases
                      open and pushes the modal down instead of popping in.
                      motion-reduce disables it for reduced-motion users. */}
                  <div
                    className={cn(
                      'grid transition-all duration-200 ease-out motion-reduce:transition-none',
                      showResults ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                    )}
                  >
                    <div className="overflow-hidden">
                      {showResults ? (
                        <CommandList>
                          {isSearching ? (
                            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Searching…
                            </div>
                          ) : (
                            <>
                              <CommandEmpty>No eligible leads found.</CommandEmpty>
                              <CommandGroup>
                                {candidates.map((candidate) => (
                                  <CommandItem
                                    key={candidate.id}
                                    value={candidate.id}
                                    onSelect={() => setSelectedDuplicate(candidate)}
                                  >
                                    <span className="min-w-0 flex-1 truncate">
                                      {formatMergeCandidateLabel(candidate)}
                                    </span>
                                    <span className="ml-1.5 shrink-0 text-xs text-muted-foreground">
                                      {candidate.stage?.name}
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      ) : null}
                    </div>
                  </div>
                </Command>
              )}
            </div>

            {isCrossContact ? (
              <div className="flex animate-in items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs leading-5 text-amber-800 fade-in-0 slide-in-from-top-1 duration-150 motion-reduce:animate-none dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Different contact — {getContactName(selectedDuplicate)} is not the survivor&apos;s
                  contact. Its tasks will be reparented to {getContactName(survivor) || 'this lead'}
                  . Tick “Allow cross-contact merge” to proceed.
                </span>
              </div>
            ) : null}

            <label
              htmlFor="allow-cross-contact-merge"
              className="flex items-start gap-2 text-sm leading-5"
            >
              <Checkbox
                id="allow-cross-contact-merge"
                checked={allowCrossContact}
                onCheckedChange={(value) => setAllowCrossContact(value === true)}
                className="mt-0.5"
              />
              <span>Allow cross-contact merge</span>
            </label>

            <div className="space-y-2">
              <Label htmlFor="merge-reason">Reason</Label>
              <Textarea
                id="merge-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Same homeowner request"
                rows={3}
                maxLength={1000}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mergeLead.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Merge duplicate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
